/**
 * Route paths and the nav/permission map. One place so links, the nav rail
 * (DashboardShell) and the permission guard (RequirePermission) never drift.
 *
 * Unlike shop/ui (one role => one fixed screen), the auditor dashboard gates
 * by PERMISSION CODE -- a session can hold any subset -- so there is no
 * single "landing path per role". Dashboard is the FIRST nav item and lives
 * at '/' (E2.1 design spec section 3/4): a reports:read session lands there
 * straight off firstAllowedRoute's ordering; a session missing reports:read
 * falls through to whichever section further down NAV_ITEMS it does hold.
 *
 * v1's Health nav item/route is REMOVED from the UI (E2.1 spec 7.1 -- v1
 * relic, owner ruling; the server's GET /health endpoint stays for ops).
 * That removes the one entry that needed no permission at all, so
 * firstAllowedRoute's floor is now ROUTES.notFound instead of a live page --
 * a session holding NONE of these permissions (e.g. an edited-down custom
 * role) has no dashboard section to land on.
 */
export const ROUTES = {
  login: '/login',
  dashboard: '/',
  reports: '/reports',
  shops: '/shops',
  shopDetail: '/shops/:id',
  owners: '/owners',
  users: '/users',
  roles: '/roles',
  config: '/config',
  potd: '/potd',
  notFound: '/not-found',
} as const;

/** Build a concrete /shops/:id path for a given shop id. */
export function shopDetailPath(id: number): string {
  return `/shops/${id}`;
}

export interface NavItemDef {
  path: string;
  label: string;
  /** Required permission code, or null when every authenticated session sees it. */
  permission: string | null;
}

/** Nav rail order == permission-fallback search order (this file's header comment). */
export const NAV_ITEMS: readonly NavItemDef[] = [
  { path: ROUTES.dashboard, label: 'Dashboard', permission: 'reports:read' },
  { path: ROUTES.reports, label: 'Shop Reports', permission: 'reports:read' },
  { path: ROUTES.shops, label: 'Shops', permission: 'shops:read' },
  { path: ROUTES.owners, label: 'Owners', permission: 'owners:read' },
  { path: ROUTES.users, label: 'Users', permission: 'users:manage' },
  { path: ROUTES.roles, label: 'Roles', permission: 'roles:manage' },
  { path: ROUTES.config, label: 'Config', permission: 'config:manage' },
  { path: ROUTES.potd, label: 'Password of the day', permission: 'potd:manage' },
];

/** The first nav route a session's permissions allow; ROUTES.notFound (no nav
 *  item needs zero permissions since Health was removed) is the floor. */
export function firstAllowedRoute(permissions: readonly string[]): string {
  const match = NAV_ITEMS.find((item) => item.permission === null || permissions.includes(item.permission));
  return match?.path ?? ROUTES.notFound;
}
